// The one stage/outcome → colour map for the whole app. Used by the dashboard
// (StageBar, ActivityChart), the table (columns.ts), and admin (activity
// legend, day trace, lead trail, hour columns) — previously each picked its
// own colours and disagreed with the others for the same stage/outcome.
//
// Rule: closed stages get the warm/earthy tones (clay/moss/stone/terracotta);
// open stages and call-level outcomes stay on the ink scale, lightest to
// darkest as work progresses; the one accent colour is reserved for the
// thing everyone's chasing — a booked meeting, whether seen as a stage or an
// outcome. That overlap is deliberate: a "booked" call outcome is what moves
// a lead into the "Booked" stage, so they share a colour on purpose. Earth
// tones are reserved for closed stages only, so they never collide with the
// in-progress ink/accent colours used for outcomes.

import { Z } from './charts/theme'
import type { Stage, Outcome } from './domain'

export const STAGE_COLOR: Record<Stage | 'Other', string> = {
  'New':               Z.ink300,
  'Calling':           Z.ink500,
  'Contacted':         Z.ink900,
  'Booked':            Z.accentDeep,
  'Handed Over':       Z.moss,
  'Disqualified':      Z.terracotta,
  'Existing Customer': Z.clay,
  'Wrong Contact':     Z.stone,
  'Other':             Z.ink200,
}

// Tailwind-class form of the same map, for places that set text colour via
// className rather than an inline style (the table's stage select). The open
// stages shift one notch darker than STAGE_COLOR's ink-300/500/900 — that
// lightest tone is only safe as a bar-segment background (StageBar picks a
// contrasting foreground for it); as foreground text on a plain background,
// ink-300 fails contrast, so the three-tier "lightest to darkest" progression
// starts at ink-500 here instead.
export const STAGE_TEXT_CLASS: Record<Stage, string> = {
  'New':               'text-ink-500',
  'Calling':           'text-ink-700',
  'Contacted':         'text-ink-900',
  'Booked':            'text-ink-1000 bg-brand border-brand font-semibold',
  'Handed Over':       'text-moss',
  'Disqualified':      'text-terracotta',
  'Existing Customer': 'text-clay',
  'Wrong Contact':     'text-stone',
}

// Safe lookup for stage values that come back from the API as a bare string
// (DashboardStage['stage']) rather than the narrow Stage union.
export function stageColor(stage: string): string {
  return (STAGE_COLOR as Record<string, string>)[stage] ?? STAGE_COLOR.Other
}

// Swedish display labels — the one place a Stage key is translated for the
// BDR. The key itself (STAGES in ./domain) stays English: it's what the API,
// filters and QUICK_VIEWS match on, and the display label must never leak
// into those comparisons.
export const STAGE_LABEL: Record<Stage | 'Other', string> = {
  'New':               'Ny',
  'Calling':           'Ringer',
  'Contacted':         'Kontaktad',
  'Booked':            'Bokad',
  'Handed Over':       'Överlämnad',
  'Disqualified':      'Diskvalificerad',
  'Existing Customer': 'Befintlig kund',
  'Wrong Contact':     'Fel kontakt',
  'Other':             'Övrigt',
}

// Older rows carry stage spellings from before the stage set was fixed, such
// as "Meeting booked". They still mean a canonical stage, so they read as its
// Swedish label rather than leaking English into lists like the dashboard drill.
const LEGACY_STAGE: Record<string, Stage> = {
  'meeting booked': 'Booked',
}

const LABEL_BY_LOWER: Record<string, string> = Object.fromEntries([
  ...Object.entries(STAGE_LABEL).map(([k, v]) => [k.toLowerCase(), v]),
  ...Object.entries(LEGACY_STAGE).map(([k, v]) => [k, STAGE_LABEL[v]]),
])

// Safe lookup for stage values that come back from the API as a bare string.
// Case-insensitive, and a value it does not know is shown as it came.
export function stageLabel(stage: string): string {
  return LABEL_BY_LOWER[stage.trim().toLowerCase()] ?? stage
}

export const STAGE_REASON: Record<Stage, string> = {
  'New':               'Unworked — lightest ink, barely present.',
  'Calling':           'Being worked right now — mid ink, draws the eye without colour.',
  'Contacted':         'Reached and still open — darkest ink, the most "live" of the open stages.',
  'Booked':            "The outcome everyone's chasing — the one accent colour on the board.",
  'Handed Over':       'Closed and successful — moss, distinct from the Booked accent.',
  'Disqualified':      'Closed and unsuccessful — terracotta, the warmest/most negative tone.',
  'Existing Customer': 'Closed, a different kind of resolution — clay, distinct from Handed Over.',
  'Wrong Contact':     'Closed on a data problem, not a sales outcome — stone, the greyest/most neutral.',
}

export const OUTCOME_COLOR: Record<Outcome, string> = {
  booked:  Z.accentDeep,
  reached: Z.ink900,
  noans:   Z.ink500,
  none:    Z.ink300,
}

export const OUTCOME_LABEL: Record<Outcome, string> = {
  reached: 'Kontakt',
  booked: 'Bokat',
  noans: 'Inget svar',
  none: 'Öppnad, inget samtal',
}

// Stack / legend order across every chart: the good outcomes first.
export const OUTCOME_ORDER: Outcome[] = ['booked', 'reached', 'noans', 'none']

export const OUTCOME_REASON: Record<Outcome, string> = {
  booked:  'Same accent as the Booked stage — a booked call is what moves a lead there.',
  reached: 'Same ink as Contacted — the call-level version of "reached and open."',
  noans:   'Mid ink, same family as Calling — a routine non-event during active calling, not a failure.',
  none:    'Lightest ink, same as New — nothing has happened yet.',
}
